import React from 'react';
import Box from '@mui/material/Box';
import useUserColor from 'hooks/users/useUserColor';
import { bucketsToRuns } from './dashboardUtils';
import ColorConstants from 'constants/ColorConstants';

const BAR_HEIGHT = 7;
const ROW_HEIGHT = 22; // aligns each bar with its assignee's avatar row
const UNCODED_GREY = ColorConstants.GRAY_200;

function CoderBar({ userId, buckets }) {
  const color = useUserColor(userId);
  const runs = bucketsToRuns(buckets);

  return (
    <Box className="dashboardCoderBar" sx={{ height: ROW_HEIGHT, display: 'flex', alignItems: 'center' }}>
      {runs.length === 0 ? (
        <Box sx={{ width: '100%', height: BAR_HEIGHT, borderRadius: '3px', backgroundColor: UNCODED_GREY }} />
      ) : (
        <Box sx={{ display: 'flex', gap: '1.5px', alignItems: 'stretch', height: BAR_HEIGHT, width: '100%' }}>
          {runs.map((run, i) => (
            <Box
              key={i}
              sx={{
                flex: run.count,
                height: '100%',
                borderRadius: '2px',
                backgroundColor: run.coded ? color : UNCODED_GREY,
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

// `coverage` is the lazily-fetched { coders } for this transcript, or
// undefined while it loads. Until it arrives the bars render grey (the
// loading state IS the empty bar), then fill in with color.
function ProgressCell({ assignees, coverage, visibleAssignees }) {
  const coders = coverage?.coders || [];

  // No one is assigned to this transcript — don't show progress bars at all.
  // (This also drops any bars from since-unassigned coders: an unassigned
  // transcript has no coverage to track.) Render an em-dash placeholder to
  // match the "Last updated" empty state rather than leaving the cell blank.
  if ((assignees || []).length === 0) {
    return <Box sx={{ fontSize: 13, color: ColorConstants.GRAY_400 }}>—</Box>;
  }

  const codersById = {};
  coders.forEach(coder => { codersById[String(coder.user_id)] = coder; });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      {visibleAssignees.map(assignee => (
        <CoderBar
          key={assignee.user_id}
          userId={assignee.user_id}
          buckets={codersById[String(assignee.user_id)]?.buckets}
        />
      ))}
    </Box>
  );
}

export default ProgressCell;
